
import styled from 'styled-components'
import { useState } from 'react'
import DefaultLayout from '../components/molecules/common/DefaultLayout'
import VoiceInput from '../components/molecules/VoiceInput'
import LabeledInput from '../components/molecules/LabeledInput'
import RecordVoiceModal from '../components/molecules/RecordVoiceModal'
import { useBack } from '../hooks/useBack'
import MainButton from '../components/atoms/MainButton'
import { createWord, uploadText } from '../api/word'
import { useNavigate } from 'react-router-dom'
import { useWordRecorder } from '../hooks/useWordRecorder'
import { tutorialPageKeys } from '../data/tutorialConfig'
import { useTutorial } from '../hooks/useTutorial'
import { tutorialImages } from '../data/tutorialImages'
import TutorialOverlay from '../components/molecules/auth/TutorialOverlay'
import { logEvent } from '../utils/eventLogger'
import LoadingOverlay from '../components/molecules/common/LoadingOverlay'
import RegistrationGuard from '../components/molecules/auth/RegistrationGuard'

const Wrapper = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  position: relative;
`
const ButtonWrapper = styled.div`
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  max-width: 600px; /* Wrapper 최대 폭과 맞춤 */
  padding: 0 20px;   /* 좌우 여유 */
`
const WordRecord = () => {
  // 튜토리얼 훅
  const page = tutorialPageKeys.WORD_RECORD;
  const { show, complete } = useTutorial(page);
  const datas = tutorialImages.WORD_RECORD.steps;

  const [isSubmitting, setIsSubmitting] = useState(false); // api 호출 상태
  const [showLoading, setShowLoading] = useState(false); // 화면에 로딩 오버레이 렌더링 여부 
  
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [status, setStatus] = useState("idle")
  const [word, setWord] = useState("")
  const [meaning, setMeaning] = useState("")
  const [audioBlob, setAudioBlob] = useState(null);
  
  const { startRecording, cancelRecording } = useWordRecorder({
    onStatusChange: (s) => {
      setStatus(s);
      // 녹음 시작 시 모달 오픈
      if (s === "recording") setIsModalOpen(true);
      // 처리 완료 후 idle이면 모달 닫기
      if (s === "idle") setIsModalOpen(false);
    },
    autoStopOnSilence: true
  });

  const handleWord = (e) => {
    setWord(e.target.value)
  }
  const handleMeaning = (e) => {
    setMeaning(e.target.value)
  }
  
  const [isVoiceInput, setIsVoiceInput] = useState(false);
  const [recordData, setRecordData] = useState({
    ipa: "",
    eng_transcript: ""
  })

  const navigate = useNavigate();
  
  const handleMicClick = async () => {
    setWord("")
    setAudioBlob(null)
    setIsVoiceInput(false)
    
    try {
      const rlt = await startRecording()
      // 취소 버튼을 눌러서 rlt가 null로 돌아온 경우, 아무것도 하지 않고 즉시 종료
      if (rlt === null) {
        // console.log("녹음이 취소되었습니다.");
        return; 
      }
      // console.log(rlt)
      const { audioBlob, ipa, eng_transcript, kr_transcript } = rlt
      setAudioBlob(audioBlob)
      setRecordData({ ipa, eng_transcript })
      setWord(kr_transcript)
      // setStatus("idle")
      setIsModalOpen(false)
      setIsVoiceInput(true)
    } catch (err) {
      console.error(err)
      // 추후 마이크 권한 거부, 장치 없음, 녹음 중 실패 등 오류 메시지 분기 처리 필요
      alert("브라우저 설정에서 마이크 접근을 허용한 후 다시 시도해주세요.");
    }
  }

  // 뒤로가기 훅 호출
  const goBack = useBack();

  // 버튼 disabled 여부
  const isDisabled = word.trim() === '' || meaning.trim() === '';

  // 저장 함수
  const handleSubmit = async() => {
    let delayTimer;

    try {
      setIsSubmitting(true); 
      delayTimer = setTimeout(() => {
      setShowLoading(true);
      }, 100);
      setShowLoading(true);
      await new Promise(requestAnimationFrame);

      // 비동기 상태 업데이트 방지를 위한 변수
      let finalRecordData = recordData;

      // 음성 파일이 없으면 uploadText api 요청
      if (!audioBlob) {
        const data = await uploadText({ text: word });
        // console.log(data)
        finalRecordData = {
          ipa: data.ipa,
          eng_transcript: data.eng_transcript
        };
        // console.log(finalRecordData)
      }

      // 1. formData 생성
      const formData = new FormData()

      // 2. 음성 파일 있으면 formData에 추가
      if (audioBlob) {
        const extension = audioBlob.type.split("/")[1];
        formData.append("audioFile", audioBlob, `recording.${extension}`);
      }

      // 3. metaData 구성
      const metaData = {
        ...finalRecordData,
        kr_transcript: audioBlob ? word : "",
        // input_text: word,
        input_text: audioBlob ? null : word,
        meaning: meaning,
        // category: "기본"
      }

      // 4. formData에 json 형태로 추가
      formData.append("metadata", new Blob([JSON.stringify(metaData)], { type: "application/json" }));
      
      // 5. API 호출
      setShowLoading(true);
      const res = await createWord(formData);
      // console.log("저장 성공:", res);
      // console.log(res.word_dicts_id)
      
      // 로그 기록
      logEvent("word_register_save", {
        wordDictId: res.word_dicts_id
      })
      
      navigate('/words')

    } catch (err) {
      console.error("저장 실패:", err);
      alert('오류가 발생했습니다. 다시 시도해주세요.');

    } finally {
      // 3. 정리: 타이머 취소 및 로딩 해제
    clearTimeout(delayTimer);
    setShowLoading(false);
    setIsSubmitting(false);
    }
  }

  const handleClearWord = () => {
    setWord("");
    setAudioBlob(null);
    setIsVoiceInput(false);
  };

  const handleCancel = () => {
    // console.log("취소 버튼 클릭");
    cancelRecording(); // 훅 내부 정리
    setIsModalOpen(false);
  }

  return (
    <DefaultLayout 
      headerType="sub" 
      title="아는 단어 기록"
      onBack={goBack}>
      <RegistrationGuard>
        <Wrapper>
          <VoiceInput 
            label="발화내용"
            placeholder="발화 내용을 입력해주세요. 예) 무무" 
            value={word} 
            onChange={handleWord}
            required={true}
            onMicClick={handleMicClick}
            onClear={handleClearWord}
            readOnly={isVoiceInput}/>
          <LabeledInput 
            label="의미단어"
            placeholder="해당 발화의 의미를 입력해주세요. 예) 물" 
            value={meaning} 
            onChange={handleMeaning} 
            required={true}/>
          <ButtonWrapper>
            <MainButton 
              $variant="primary"
              title="저장"
              disabled={isDisabled}
              onClick={handleSubmit}/>
          </ButtonWrapper>
        </Wrapper>
        {isModalOpen && 
          <RecordVoiceModal 
            status={status}
            onRetry={handleMicClick}
            onCancel={handleCancel}/>}
        {showLoading && <LoadingOverlay/>}
      </RegistrationGuard>
      {show && <TutorialOverlay datas={datas} onClose={complete} />}
    </DefaultLayout>
  )
}

export default WordRecord